import * as React from "react";
import { useEffect } from "react";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { Loader } from "../Loader";
import moment from "moment";
import { TranslateText } from "../TranslateText";
import * as am4plugins_overlapBuster from "@amcharts/amcharts4/plugins/overlapBuster";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const scrollbarConfig = {
  default: 0,
  value: 30,
  gripSize: 25,
  opacity: 1,
  strokeOpacity: 0.8,
  saturation: 0.4,
  alignCenter: "center",
  alignMiddle: "middle",
  dateFormatter: "yyyy-MM-dd HH:mm"
};
const chartConfig = {
  enabled: true,
  default: 0,
  fontSize: 11,
  strokeOpactiy: 1,
  strokeWidth: 3,
  tensionX: 1
};
export const groupBy = (items, key) => items.length ? items.reduce((result, item) => ({
  ...result,
  [item[key]]: [...(result[item[key]] || []), item]
}), {}) : {};
const LineChart = ({
  value,
  stream,
  xAxisDateFormat,
  stackValue,
  slider,
  stepLineChart,
  target
}) => {
  const [inActiveLegend, setInactiveLegend] = React.useState([]);
  const randomID = (Math.random() + 1).toString(36).substring(7);
  let chart = React.useRef(am4core.create(randomID, am4charts.XYChart));
  const bodyStyles = window.getComputedStyle(document.body);
  const defaultColor = bodyStyles.getPropertyValue("--kc-line-chart-color");
  const scrollGripColor = bodyStyles.getPropertyValue("--kc-chart-scrollbar-grip-color");
  const scrollGripColorBg = bodyStyles.getPropertyValue("--kc-chart-scrollbar-bg-color");
  useEffect(() => {
    // Themes begin
    am4core.useTheme(am4themes_animated);
    // Themes end

    chart.current = am4core.create(randomID, am4charts.XYChart);
    if (chart.current.logo) {
      chart.current.logo.disabled = true;
    }
    chart.current.zoomOutButton.disabled = true;
    const groupByLines = value?.length ? groupBy(value, 'value') : {};
    const objectKeys = Object.keys(groupByLines);
    const stackLegent = stackValue;
    let formattedValue = [];
    for (let index = 0; index < value.length; index++) {
      const element = value[index];
      formattedValue.push({
        ...element,
        [`x${element.value}`]: element.x,
        [`y${element.value}`]: element.y
      });
    }
    chart.current.data = formattedValue;

    // helper function to check if a value is a valid date
    function isDate(value) {
      return value instanceof Date && !isNaN(value.getTime());
    }
    const firstDataPoint = value && value.length ? value[0] : {
      x: new Date()
    };
    const isDateXAxis = firstDataPoint && isDate(firstDataPoint?.x);
    const isStringXAxis = typeof firstDataPoint?.x === "string";

    // Create axes
    let dateAxis;
    if (isDateXAxis) {
      dateAxis = chart.current.xAxes.push(new am4charts.DateAxis()); // for date
    } else if (isStringXAxis) {
      dateAxis = chart.current.xAxes.push(new am4charts.CategoryAxis()); // for string values
    } else {
      dateAxis = chart.current.xAxes.push(new am4charts.ValueAxis());
    }
    dateAxis.renderer.grid.template.location = 0;
    dateAxis.renderer.minGridDistance = 10;
    dateAxis.renderer.labels.template.rotation = -45;
    dateAxis.renderer.labels.template.horizontalCenter = "middle";
    dateAxis.renderer.labels.template.verticalCenter = "middle";
    if (isDateXAxis) {
      dateAxis.dateFormats.setKey("second", xAxisDateFormat);
      dateAxis.periodChangeDateFormats.setKey("second", xAxisDateFormat);
      dateAxis.dateFormatter.dateFormat = xAxisDateFormat;
      dateAxis.skipEmptyPeriods = true; // enable this to show exact value from data including seconds
      dateAxis.renderer.labels.template.adapter.add("text", (_, target) => {
        if (target?.dataItem?.date) {
          let md = moment(target.dataItem.date).format(xAxisDateFormat);
          return md;
        }
      });
      dateAxis.baseInterval = {
        timeUnit: "second",
        count: 10
      };
    }
    const valueAxis = chart.current.yAxes.push(new am4charts.ValueAxis());
    valueAxis.renderer.labels.template.fill = am4core.color(defaultColor);
    valueAxis.renderer.grid.template.stroke = am4core.color(defaultColor);
    valueAxis.renderer.labels.template.fontSize = chartConfig.fontSize;
    if (valueAxis && valueAxis.tooltip) {
      valueAxis.tooltip.disabled = true;
    }
    dateAxis.skipEmptyPeriods = true;
    dateAxis.renderer.labels.template.fill = am4core.color(defaultColor);
    dateAxis.renderer.labels.template.fill = am4core.color(defaultColor);
    dateAxis.renderer.labels.template.fontSize = chartConfig.fontSize;
    valueAxis.renderer.labels.template.fontSize = chartConfig.fontSize;
    valueAxis.renderer.grid.template.strokeOpacity = chartConfig.default;
    chart.current.zoomOutButton.disabled = true;
    let series = {};
    let circleBullet = {};
    let overlap = chart.current.plugins.push(new am4plugins_overlapBuster.OverlapBuster());
    for (let index = 0; index < objectKeys.length; index++) {
      const element = objectKeys[index];
      series[element] = stepLineChart ? chart.current.series.push(new am4charts.StepLineSeries()) : chart.current.series.push(new am4charts.LineSeries());
      circleBullet[element] = series[element].bullets.push(new am4charts.CircleBullet());
    }
    for (var i = 0; i < objectKeys.length; i++) {
      const name = objectKeys[i];
      const element = groupByLines[objectKeys[i]];
      const stackElement = stackLegent?.filter(index => index.value === name);
      const stackValue = stackElement?.length ? stackElement[0] : {
        value: element.value,
        label: '',
        color: defaultColor,
        lineTension: 1,
        strokeWidth: 1
      };
      series[name].name = stackValue.label;
      if (isDateXAxis) {
        series[name].dataFields.dateX = `x${name}`;
      } else if (isStringXAxis) {
        series[name].dataFields.categoryX = [`x${name}`]; // Use category data field for string values
      } else {
        series[name].dataFields.valueX = [`x${name}`]; // Use category data field for string values
      }

      series[name].dataFields.valueY = `y${name}`;
      series[name].dataFields.unitValue = "yUnit";
      series[name].interpolationDuration = 500;
      series[name].tooltipText = isDateXAxis ? "{name}: {valueY} {yUnit}, {dateX}[/]" : isStringXAxis ? "{name}: {valueY} {yUnit}, {categoryX}[/]" : "{name}: {valueY} {yUnit}, {valueX}[/]";
      series[name].strokeWidth = stackValue.strokeWidth;
      series[name].defaultState.transitionDuration = 10;
      series[name].tensionX = stackValue.lineTension;
      series[name].tooltip = new am4core.Tooltip();
      series[name].tooltip.getFillFromObject = false;
      series[name].cursorTooltipEnabled = true;
      series[name].stroke = am4core.color(stackValue.color);
      series[name].strokeWidth = stackValue.strokeWidth;
      series[name].fill = am4core.color(stackValue.color);
      //custom color for tooltip
      series[name].tooltip.background.fill = am4core.color(stackValue.color);
      series[name].tooltip.label.fill = am4core.color("#fff");
      circleBullet[name].circle.stroke = am4core.color("#fff");
      circleBullet[name].circle.strokeWidth = 0;
      circleBullet[name].circle.fill = am4core.color(stackValue.color);
      circleBullet[name].tooltipText = isDateXAxis ? "{name}: {valueY} {yUnit}, {dateX}[/]" : isStringXAxis ? "{name}: {valueY} {yUnit}, {categoryX}[/]" : "{name}: {valueY} {yUnit}, {valueX}[/]";
      circleBullet[name].circle.radius = 5;
      series[name].tooltip.label.adapter.add("textOutput", function (text, target) {
        const filteredItem = chart.current?.data.filter(i => moment(i.x).format("X") === moment(target.dataItem?._dataContext?.x).format("X") && i.y === target.dataItem?._dataContext?.y) || [];
        if (filteredItem?.length) {
          let textValueFormation = '';
          for (let index = 0; index < filteredItem.length; index++) {
            const element = filteredItem[index];
            const stackElement = stackLegent?.filter(index => index.value === element.value);
            const stackValue = stackElement?.length ? stackElement[0] : {
              value: element.value,
              label: element.value,
              color: defaultColor,
              lineTension: 1,
              strokeWidth: 1
            };
            let activeLegends = inActiveLegend.filter(i => i === stackValue.label) || [];
            if (!activeLegends.length) {
              textValueFormation += `${textValueFormation === '' ? '' : '\n'} ${stackValue.label}: ${element.y} ${element.yUnit}, ${moment(element.x).format(xAxisDateFormat)}[/]`;
            }
          }
          return textValueFormation;
        } else {
          return text;
        }
      });
      overlap.targets.push(circleBullet[name]);
    }
    chart.current.legend = new am4charts.Legend();
    chart.current.legend.useDefaultMarker = true;
    chart.current.legend.position = "top";
    chart.current.legend.contentAlign = "left";
    chart.current.legend.labels.template.fill = am4core.color(defaultColor);
    chart.current.legend.itemContainers.template.marginBottom = 20;
    chart.current.legend.itemContainers.template.events.on("hit", function (ev) {
      let seriesColumn = ev.target.dataItem.dataContext;
      seriesColumn.isActive = !seriesColumn.isActive;
      let inActiveLegendValues = inActiveLegend;
      if (seriesColumn.isActive) {
        inActiveLegendValues.push(ev?.target?.dataItem?.dataContext?.name);
      } else {
        const findIndex = inActiveLegendValues.findIndex(i => i === ev?.target?.dataItem?.dataContext?.name);
        inActiveLegendValues.splice(findIndex, 1);
      }
      setInactiveLegend(inActiveLegendValues);
    });
    const marker = chart.current.legend.markers.template;

    // Add custom image instead
    const checkbox = marker.createChild(am4core.Image);
    checkbox.width = 23;
    checkbox.height = 23;
    checkbox.verticalCenter = "top";
    checkbox.horizontalCenter = "left";
    checkbox.href = "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4NCjxzdmcgd2lkdGg9IjI0cHgiIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAwIDI0IDI0IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPg0KICAgIDwhLS0gR2VuZXJhdG9yOiBTa2V0Y2ggNTIuNiAoNjc0OTEpIC0gaHR0cDovL3d3dy5ib2hlbWlhbmNvZGluZy5jb20vc2tldGNoIC0tPg0KICAgIDx0aXRsZT5pY29ucy9kYXJrLVVJL2NoZWNrLW9uX0E8L3RpdGxlPg0KICAgIDxkZXNjPkNyZWF0ZWQgd2l0aCBTa2V0Y2guPC9kZXNjPg0KICAgIDxnIGlkPSJpY29ucy9kYXJrLVVJL2NoZWNrLW9uX0EiIHN0cm9rZT0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIxIiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPg0KICAgICAgICA8cmVjdCBpZD0iUmVjdGFuZ2xlLTUiIGZpbGwtb3BhY2l0eT0iMC4xIiBmaWxsPSIjRkZGRkZGIiBmaWxsLXJ1bGU9Im5vbnplcm8iIHg9IjAiIHk9IjAiIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgcng9IjIiPjwvcmVjdD4NCiAgICAgICAgPHBvbHlnb24gaWQ9IlBhdGgiIGZpbGw9IiNGRkZGRkYiIGZpbGwtcnVsZT0ibm9uemVybyIgcG9pbnRzPSIxNyA3LjUgMTEgMTMuNSA4IDEwLjUgNi41IDEyIDExIDE2LjUgMTguNSA5Ij48L3BvbHlnb24+DQogICAgPC9nPg0KPC9zdmc+";
    checkbox.dx = 0;
    checkbox.dy = 0;
    const checkboxActiveState = checkbox.states.create("active");
    checkboxActiveState.properties.opacity = 0;
    const seriesRangeDataItem = valueAxis.axisRanges.create();
    if (target.value > 1) {
      seriesRangeDataItem.value = target.value; //target value
      seriesRangeDataItem.endValue = target.value;
      seriesRangeDataItem.grid.strokeOpacity = chartConfig.strokeOpactiy;
      seriesRangeDataItem.grid.strokeWidth = target.strokeWidth;
      seriesRangeDataItem.grid.stroke = am4core.color(target.color);
      seriesRangeDataItem.grid.visible = chartConfig.enabled;
      seriesRangeDataItem.label.text = "";
      seriesRangeDataItem.label.location = chartConfig.default;
      seriesRangeDataItem.label.inside = chartConfig.enabled;
      seriesRangeDataItem.label.horizontalCenter = "middle";
      seriesRangeDataItem.label.verticalCenter = "bottom";
      seriesRangeDataItem.label.fontWeight = "normal";
      seriesRangeDataItem.label.fill = am4core.color(defaultColor);
    }

    //scrollbar slider config
    if (slider) {
      chart.current.scrollbarX = new am4core.Scrollbar();
      chart.current.scrollbarX.marginLeft = scrollbarConfig.default;
      chart.current.scrollbarX.marginBottom = scrollbarConfig.value;
      function customizeGrip(grip) {
        grip.showSystemTooltip = false;
        grip.icon.disabled = true;
        grip.background.disabled = true;
        grip.background.strokeOpacity = scrollbarConfig.default;
        let img = grip.createChild(am4core.Circle);
        img.width = scrollbarConfig.gripSize;
        img.height = scrollbarConfig.gripSize;
        img.fill = am4core.color(scrollGripColor);
        img.align = scrollbarConfig.alignCenter;
        img.valign = scrollbarConfig.alignMiddle;
      }
      chart.current.scrollbarX.thumb.showSystemTooltip = false;
      chart.current.scrollbarX.thumb.background.fill = am4core.color(scrollGripColorBg);
      chart.current.scrollbarX.thumb.background.fillOpacity = scrollbarConfig.opacity;
      chart.current.scrollbarX.thumb.background.stroke = am4core.color(scrollGripColorBg);
      chart.current.scrollbarX.thumb.background.strokeWidth = scrollbarConfig.default;
      chart.current.scrollbarX.thumb.background.strokeOpacity = scrollbarConfig.strokeOpacity;

      // below config for scrollbar grip hover color
      let hoverState = chart.current.scrollbarX.thumb.background.states.create("hover");
      let hoverDown = chart.current.scrollbarX.thumb.background.states.create("down");
      hoverState.properties.fill = am4core.color(scrollGripColorBg);
      hoverDown.properties.fill = am4core.color(scrollGripColorBg);
      customizeGrip(chart.current.scrollbarX.startGrip);
      customizeGrip(chart.current.scrollbarX.endGrip);
      chart.current.scrollbarX.startGrip.disabled = !slider;
      chart.current.scrollbarX.endGrip.disabled = !slider;
      chart.current.hiddenState.properties.opacity = scrollbarConfig.opacity;
      chart.current.paddingRight = scrollbarConfig.value;
      chart.current.dateFormatter.inputDateFormat = scrollbarConfig.dateFormatter;
      var colorSet = new am4core.ColorSet();
      colorSet.saturation = scrollbarConfig.saturation;
    }

    // Clean up when the component unmounts
    return () => {
      chart.current.dispose();
    };
  }, [value]);
  useEffect(() => {
    let streamVal = [];
    for (let index = 0; index < stream.length; index++) {
      const element = stream[index];
      streamVal.push({
        ...element,
        [`x${element.value}`]: element.x,
        [`y${element.value}`]: element.y
      });
    }
    chart.current.addData(streamVal);
  }, [stream]);
  return /*#__PURE__*/_jsx("div", {
    className: "kc-ui-chart",
    id: randomID
  });
};
const _LineChart = props => {
  const {
    title,
    width,
    disabled,
    loader,
    value,
    stream,
    noValueText,
    xAxisDateFormat,
    stack,
    slider,
    stepLineChart,
    target
  } = props;
  const [chartValue, setChartValue] = React.useState(value);
  const [loading, setLoading] = React.useState(false);
  const [streamValue, setStreamValue] = React.useState(stream);
  React.useEffect(() => {
    setChartValue(value);
    setLoading(loader);
  }, [value, loader]);
  React.useEffect(() => {
    if (!chartValue || !chartValue.length) {
      setChartValue(stream);
    } else {
      setStreamValue(stream);
    }
  }, [stream]);
  return /*#__PURE__*/_jsx(React.Fragment, {
    children: /*#__PURE__*/_jsx("div", {
      className: `kc-ui-colm kc-ui-line-chart ${disabled && "kc-ui-disabled "}`,
      style: {
        width: `${width}`
      },
      children: /*#__PURE__*/_jsxs("div", {
        className: "kc-ui-colm-div",
        children: [title ? /*#__PURE__*/_jsx("h4", {
          className: "kc-ui-chart-title",
          children: /*#__PURE__*/_jsx(TranslateText, {
            id: title
          })
        }) : null, loading ? /*#__PURE__*/_jsx(Loader, {
          loader: loading
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [chartValue.length ? /*#__PURE__*/_jsx("div", {
            className: "kc-ui-line-chart-div",
            children: /*#__PURE__*/_jsx(LineChart, {
              value: chartValue,
              stream: streamValue,
              xAxisDateFormat: xAxisDateFormat,
              stackValue: stack,
              slider: slider,
              stepLineChart: stepLineChart,
              target: target
            })
          }) : null, !chartValue || !chartValue.length || typeof chartValue !== "object" && noValueText ? /*#__PURE__*/_jsx("p", {
            className: "kc-ui-align-no-result kc-ui-align-center",
            children: noValueText
          }) : null]
        })]
      })
    })
  });
};
_LineChart.defaultProps = {
  title: "",
  width: "100%",
  value: [],
  stack: [],
  stream: [],
  target: {},
  loader: false,
  stepLineChart: false,
  slider: false,
  xAxisDateFormat: "HH:mm:ss",
  noValueText: "",
  disabled: false
};
export const LineChartWidget = /*#__PURE__*/React.memo(_LineChart);